// Pure trend-regime math over a weekly close series.
//
// Seasonal trend   : price vs its 52-week simple moving average.
// Generational trend: price vs its 200-week simple moving average.
//   price >= MA → bullish, price < MA → bearish.
// Seasonal rows are ranked by the 52-week Sharpe ratio; generational rows by the
// 200-week Sharpe ratio. Sharpe is annualized from weekly simple returns, rf=0.
//
// Rate symbols (^TNX/^TYX) carry a yield level in `close`, so their read is a
// rising- vs falling-yield trend and Sharpe is risk-adjusted yield momentum.

import type { AssetDef, CloseSeries } from "../api/data";

export const SEASONAL_WEEKS = 52;
export const GENERATIONAL_WEEKS = 200;
const WEEKS_PER_YEAR = 52;
// How far back to measure the average's slope, per window. The 1 Year average is
// slower, so a quarter's lookback; the 4 Year average a full year.
const SEASONAL_SLOPE_LOOKBACK = 13;
const GENERATIONAL_SLOPE_LOOKBACK = 52;

export type Trend = "bullish" | "bearish";

export interface TrendRead {
  /** null when there aren't enough bars for this window. */
  trend: Trend | null;
  /** Moving average over the window. */
  ma: number;
  /** Signed distance of price from the MA, as a fraction (0.05 = +5%). */
  distance: number;
  /** Annualized Sharpe of weekly returns over the window (rf=0). */
  sharpe: number;
  /** Annualized slope of the window's average — its % rise/fall per year,
   *  measured over a recent lookback. Positive = the average is turning up. */
  slope: number;
  /** How many weeks the current trend has persisted — weeks since price last
   *  crossed the average. null when there isn't enough history to place the
   *  average at the latest bar. Floored by the computable history. */
  ageWeeks: number | null;
  /** Closes over the window, ascending — for the sparkline. */
  spark: number[];
}

export interface AssetContext {
  def: AssetDef;
  price: number;
  /** Latest bar timestamp (epoch milliseconds). */
  asOf: number;
  seasonal: TrendRead;
  generational: TrendRead;
}

export interface AssetContextError {
  def: AssetDef;
  error: string;
}

export type AssetResult = AssetContext | AssetContextError;

export function isError(r: AssetResult): r is AssetContextError {
  return "error" in r;
}

function mean(xs: number[]): number {
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

/** Sample standard deviation (n-1). Returns 0 for <2 points. */
function stdev(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  const v = xs.reduce((a, b) => a + (b - m) * (b - m), 0) / (xs.length - 1);
  return Math.sqrt(v);
}

/** Weekly simple returns from a close series. */
function weeklyReturns(closes: number[]): number[] {
  const r: number[] = [];
  for (let i = 1; i < closes.length; i++) {
    const prev = closes[i - 1];
    if (prev !== 0) r.push(closes[i] / prev - 1);
  }
  return r;
}

/** Annualized Sharpe (rf=0) of the last `window` weekly returns. */
function windowSharpe(closes: number[], window: number): number {
  const rets = weeklyReturns(closes).slice(-window);
  if (rets.length < 2) return 0;
  const sd = stdev(rets);
  if (sd === 0) return 0;
  return (mean(rets) / sd) * Math.sqrt(WEEKS_PER_YEAR);
}

/** Annualized slope of the `window`-period average: compare the average now to
 *  the average `lookback` bars ago, as a fractional change, annualized. 0 when
 *  there isn't enough history to place the average `lookback` bars back. */
function windowSlope(closes: number[], window: number, lookback: number): number {
  const n = closes.length;
  if (n < window + lookback) return 0;
  const maNow = mean(closes.slice(n - window));
  const maPast = mean(closes.slice(n - window - lookback, n - lookback));
  if (maPast === 0) return 0;
  return (maNow / maPast - 1) * (WEEKS_PER_YEAR / lookback);
}

/** Weeks the current trend has persisted: how many bars, counting back from the
 *  latest, price has stayed on its current side of the `window`-period average —
 *  i.e. weeks since price last crossed the average. Floored by the computable
 *  history (the average only exists from bar `window-1` on). null if there isn't
 *  even one bar with an average. */
function windowTrendAge(closes: number[], window: number): number | null {
  const n = closes.length;
  if (n < window) return null;
  // prefix[i] = sum of closes[0..i-1] → SMA ending at bar i is O(1).
  const prefix = new Array<number>(n + 1);
  prefix[0] = 0;
  for (let i = 0; i < n; i++) prefix[i + 1] = prefix[i] + closes[i];
  const maAt = (i: number) => (prefix[i + 1] - prefix[i + 1 - window]) / window;
  const side = (i: number) => (closes[i] >= maAt(i) ? 1 : -1);
  const last = n - 1;
  const cur = side(last);
  let age = 1; // the latest bar itself is one week into the trend
  for (let i = last - 1; i >= window - 1; i--) {
    if (side(i) !== cur) break;
    age++;
  }
  return age;
}

function readWindow(closes: number[], window: number, slopeLookback: number): TrendRead {
  const price = closes[closes.length - 1];
  const slope = windowSlope(closes, window, slopeLookback);
  const ageWeeks = windowTrendAge(closes, window);
  if (closes.length < window) {
    // Not enough history — leave trend null but still surface what we can.
    const spark = closes.slice(-window);
    const ma = spark.length ? mean(spark) : price;
    return { trend: null, ma, distance: ma ? price / ma - 1 : 0, sharpe: windowSharpe(closes, window), slope, ageWeeks, spark };
  }
  const spark = closes.slice(-window);
  const ma = mean(spark);
  const distance = ma ? price / ma - 1 : 0;
  return {
    trend: price >= ma ? "bullish" : "bearish",
    ma,
    distance,
    sharpe: windowSharpe(closes, window),
    slope,
    ageWeeks,
    spark,
  };
}

export function computeContext(def: AssetDef, series: CloseSeries): AssetContext {
  const closes = series.close;
  return {
    def,
    price: closes[closes.length - 1],
    asOf: series.t[series.t.length - 1],
    seasonal: readWindow(closes, SEASONAL_WEEKS, SEASONAL_SLOPE_LOOKBACK),
    generational: readWindow(closes, GENERATIONAL_WEEKS, GENERATIONAL_SLOPE_LOOKBACK),
  };
}

/** Map |distance| to a 1–4 dot-ramp strength (matches the Ratio Flow rows). */
export function strengthDots(distance: number): number {
  const d = Math.abs(distance);
  if (d >= 0.15) return 4;
  if (d >= 0.07) return 3;
  if (d >= 0.02) return 2;
  return 1;
}
